import { createInstance, MatomoProvider } from '@datapunt/matomo-tracker-react';
import CssBaseline from '@material-ui/core/CssBaseline';
import { ThemeProvider } from '@material-ui/core/styles';
import { Provider as RollbarProvider } from '@rollbar/react';
import { Authenticator } from 'aws-amplify-react';
import { ComponentProps, Fragment, PropsWithChildren } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';

import PrivateHome from './private/components/PrivateHome/private-home';
import PublicHome from './public/components/PublicHome/public-home';
import PublicMentionAccept from './public/components/PublicMentionAccept/public-mention-accept';
import { rollbarConfig } from './rollbar-config';
import { myAmplifySignupConfig, myAmplifyTheme } from './shared/components/auth/amplify-config';
import { CustomConfirmSignIn } from './shared/components/auth/custom-confirm-sign-in';
import { CustomConfirmSignUp } from './shared/components/auth/custom-confirm-sign-up';
import { CustomForgotPassword } from './shared/components/auth/custom-forgot-password';
import { CustomSignIn } from './shared/components/auth/custom-sign-in';
import { CustomSignUp } from './shared/components/auth/custom-sign-up';
import Footer from './shared/components/layout/footer/footer';
import Navbar from './shared/components/layout/navbar/navbar';
import { persist, store } from './store';
import theme from './styles/theme';

const MatomoProviderWithChildren = MatomoProvider as (props: PropsWithChildren<ComponentProps<typeof MatomoProvider>>) => JSX.Element;
const AuthenticatorWithChildren = Authenticator as unknown as (
  props: PropsWithChildren<ComponentProps<typeof Authenticator>>
) => JSX.Element;

const PrivateHomeAuthGate = ({ authState }: { authState?: string }): JSX.Element | null => {
  if (authState !== 'signedIn') {
    return null;
  }

  return <PrivateHome />;
};

const instance = createInstance({
  urlBase: 'https://analytics.comptroller.texas.gov',
  siteId: process.env.REACT_APP_ENV === 'prod' ? 34 : 35,
  linkTracking: true
});

const App = (): JSX.Element => {
  return (
    <MatomoProviderWithChildren value={instance}>
      <RollbarProvider config={rollbarConfig}>
        <ThemeProvider theme={theme}>
          <Fragment>
            <CssBaseline />
            <ReduxProvider store={store}>
              <BrowserRouter>
                <PersistGate persistor={persist}>
                  <Navbar />
                  <Routes>
                    <Route element={<PublicHome />} path="/" />
                    <Route
                      element={
                        <AuthenticatorWithChildren hideDefault={true} signUpConfig={myAmplifySignupConfig} theme={myAmplifyTheme}>
                          <CustomSignIn />
                          <CustomSignUp />
                          <CustomConfirmSignIn />
                          <CustomConfirmSignUp />
                          <CustomForgotPassword />
                          <PrivateHomeAuthGate />
                        </AuthenticatorWithChildren>
                      }
                      path="/applications/*"
                    />
                    <Route element={<PublicMentionAccept />} path="/acknowledge" />
                  </Routes>
                  <Footer />
                </PersistGate>
              </BrowserRouter>
            </ReduxProvider>
          </Fragment>
        </ThemeProvider>
      </RollbarProvider>
    </MatomoProviderWithChildren>
  );
};

export default App;
